import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import LandCard from "../../components/landComponents/LandCard";
import Pagination from "../../components/Pagination";
import { entityMap } from "../../utils/mappings";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const LandLookup = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const [lands, setLands] = useState([]);
  const [handles, setGuildHandles] = useState([]);
  const [filter, setFilter] = useState(query.get("search") || "");
  const [landNum, setLandNum] = useState("");
  const [environment, setEnvironment] = useState("");
  const [landSize, setLandSize] = useState("");
  const [houseSize, setHouseSize] = useState("");
  const [guild, setGuild] = useState("");
  const [treeDensity, setTreeDensity] = useState("");
  const [permissions, setPermissions] = useState("");
  const [industryFilters, setIndustryFilters] = useState([{ industryName: "", minAmount: 0 }]);
  const [viewMode, setViewMode] = useState("default"); // 'default' or 'minimal'

  const [currentPage, setCurrentPage] = useState(parseInt(query.get("page") || "1", 10));
  const itemsPerPage = 20;

  const categoryOptions = useMemo(
    () =>
      Object.keys(entityMap).map((category) => ({
        value: category,
        label: category,
      })),
    []
  );

  // Generate industry options grouped by category
  const countEntities = (land, entityName) => {
    let count = 0;

    // Check land.entities
    if (land.entities?.length > 0) {
      const entity = land.entities[0];
      count += entity.entItems?.[entityName] || 0;
      count += entity.bstItems?.[entityName] || 0;
      count += entity.otherEntities?.[entityName] || 0;
    }

    // Check houseEntities
    if (land.houseEntities?.length > 0) {
      const houseEntity = land.houseEntities[0];
      count += houseEntity.entItems?.[entityName] || 0;
      count += houseEntity.bstItems?.[entityName] || 0;
      count += houseEntity.otherEntities?.[entityName] || 0;
    }

    return count;
  };

  const countTreesOfTier = (land, tier) => {
    let total = 0;
    const treePattern = new RegExp(`^ent_tree(?:Land|Water|Space)${tier}v\\d$`);

    // Check land.entities
    if (land.entities?.length > 0) {
      const entity = land.entities[0];
      // Check entItems
      for (const [entityName, quantity] of Object.entries(entity.entItems || {})) {
        if (treePattern.test(entityName)) {
          total += quantity;
        }
      }
      // Also check otherEntities for trees (if needed)
      for (const [entityName, quantity] of Object.entries(entity.otherEntities || {})) {
        if (treePattern.test(entityName)) {
          total += quantity;
        }
      }
    }

    // Check houseEntities
    if (land.houseEntities?.length > 0) {
      const houseEntity = land.houseEntities[0];
      // Check entItems
      for (const [entityName, quantity] of Object.entries(houseEntity.entItems || {})) {
        if (treePattern.test(entityName)) {
          total += quantity;
        }
      }
      // Also check otherEntities for trees (if needed)
      for (const [entityName, quantity] of Object.entries(houseEntity.otherEntities || {})) {
        if (treePattern.test(entityName)) {
          total += quantity;
        }
      }
    }

    return total;
  };

  // Update the industry options to include all entity types
  const industryOptions = useMemo(() => {
    const groupedOptions = {};
    const treeVariantsAdded = new Set();

    // Process boosts first
    groupedOptions["Boosts"] = Object.entries(entityMap.Boosts).map(([value, { name }]) => ({
      value,
      label: name,
      category: "Boosts",
      isTree: false,
    }));

    // Process other categories
    Object.entries(entityMap).forEach(([category, items]) => {
      if (category === "Boosts") return; // Already processed

      groupedOptions[category] = [];

      Object.entries(items).forEach(([value, { name }]) => {
        if (name === "Tree") {
          const treeKey = `${category}_Tree`;
          if (!treeVariantsAdded.has(treeKey)) {
            groupedOptions[category].push({
              value: `tree_${category}`,
              label: `Tree ${category}`,
              category,
              isTree: true,
            });
            treeVariantsAdded.add(treeKey);
          }
        } else {
          groupedOptions[category].push({
            value,
            label: name,
            category,
            isTree: false,
          });
        }
      });
    });

    return groupedOptions;
  }, []);

  const handleCategoryChange = (index, selectedCategory) => {
    const updatedFilters = [...industryFilters];
    updatedFilters[index] = {
      category: selectedCategory?.value || "",
      industryName: "",
      minAmount: updatedFilters[index].minAmount,
    };
    setIndustryFilters(updatedFilters);
  };

  // Filter functions
  const checkIndustryFilters = (land) => {
    if (!industryFilters.length) return true;

    return industryFilters.every(({ category, industryName, minAmount }) => {
      if (!industryName || minAmount <= 0) return true;

      // Special handling for trees
      if (industryName.includes("tree")) {
        const tierMatch = category.match(/T(\d)/);
        if (tierMatch) {
          const tier = tierMatch[1];
          return countTreesOfTier(land, tier) >= minAmount;
        }
      }

      // Regular entity check
      return countEntities(land, industryName) >= minAmount;
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, landNum, environment, landSize, houseSize, guild, treeDensity, permissions]);

  const filteredLands = lands.filter((land) => {
    const matchesTextFilter =
      !filter || land.landName?.toLowerCase().includes(filter.toLowerCase()) || land.owner?.toLowerCase().includes(filter.toLowerCase());

    const matchesLandNum =
      !landNum ||
      String(land.landNum || "")
        .toLowerCase()
        .includes(landNum.toLowerCase());

    const matchesEnvironment = !environment || land.environment === environment;
    const matchesLandSize = !landSize || land.landSize === landSize;
    const matchesHouseSize = !houseSize || land.houseSize === houseSize;
    const matchesGuild = !guild || land.guild === guild;
    const matchesTreeDensity = !treeDensity || land.treeDensity === treeDensity;

    const matchesPermissions = !permissions || (land.permissions && land.permissions.includes(permissions));
    const matchesIndustry = checkIndustryFilters(land);

    return (
      matchesTextFilter &&
      matchesLandNum &&
      matchesEnvironment &&
      matchesLandSize &&
      matchesHouseSize &&
      matchesGuild &&
      matchesTreeDensity &&
      matchesPermissions &&
      matchesIndustry
    );
  });

  // Pagination calculations
  const totalItems = filteredLands.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLands = filteredLands.slice(indexOfFirstItem, indexOfLastItem);
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  // Effects
  useEffect(() => {
    const params = new URLSearchParams();

    if (filter) params.set("search", filter);
    if (currentPage !== 1) params.set("page", currentPage);

    navigate({ search: params.toString() });
  }, [filter, currentPage, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const landResponse = await axios.get("https://api.pixelore.wiki/api/lands");
        const guildResponse = await axios.get("https://api.pixelore.wiki/api/guilds");

        // Get guild handles from guild data and sort alphabetically
        const guildHandles = guildResponse.data.map((guild) => guild.handle).sort((a, b) => a.localeCompare(b)); // Alphabetical sort

        // Get unique guilds from land data
        const landGuilds = new Set(landResponse.data.map((land) => land.guild));

        // Find common guild handles (already sorted alphabetically)
        const commonGuilds = guildHandles.filter((handle) => landGuilds.has(handle));
        setGuildHandles(commonGuilds);

        // Sort lands numerically by landNum (1 -> 5000)
        const sortedLands = landResponse.data.sort((a, b) => {
          return a.landNum - b.landNum; // Numeric sort
        });
        setLands(sortedLands);
      } catch (error) {
        console.log("Issue fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  const uniquePermissions = useMemo(() => {
    const allPermissions = lands.flatMap((land) => land.permissions || []);
    return [...new Set(allPermissions)].sort();
  }, [lands]);

  // Clear all filters
  const clearFilters = () => {
    setFilter("");
    setLandNum("");
    setEnvironment("");
    setLandSize("");
    setHouseSize("");
    setGuild("");
    setTreeDensity("");
    setPermissions("");
    setIndustryFilters([{ category: "", industryName: "", minAmount: 0 }]);
  };

  return (
    <div className="panel-style max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="panel-heading">Lands Lookup</h1>

      {/* Filters Section */}
      <div className="filters-panel rounded-lg shadow p-4 mb-1 text-xs">
        <h2 className="text-xl font-semibold">Filters</h2>
        <p className="text-xs text-gray-400 my-auto mb-2">({filteredLands.length} Lands Filtered)</p>

        {/* Compact Filter Grid - Single row on desktop, stacked on mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-2 mb-2">
          {/* Land Number */}
          {/* Land Number */}
          <div>
            <label className="block text-xs font-medium mb-1">Land #</label>
            <input type="text" placeholder="#" value={landNum} onChange={(e) => setLandNum(e.target.value)} className="select-forms px-2 py-1 w-full text-sm" />
          </div>

          {/* Environment */}
          <div>
            <label className="block text-xs font-medium mb-1">Environment</label>
            <select value={environment} onChange={(e) => setEnvironment(e.target.value)} className="select-forms px-2 py-1 w-full">
              <option value="">All</option>
              <option value="Land">Grass</option>
              <option value="Water">Water</option>
              <option value="Space">Space</option>
            </select>
          </div>

          {/* Land Size */}
          <div>
            <label className="block text-xs font-medium mb-1">Land Size</label>
            <select value={landSize} onChange={(e) => setLandSize(e.target.value)} className="select-forms px-2 py-1 w-full">
              <option value="">All</option>
              <option value="Small">Small</option>
              <option value="Large">Large</option>
            </select>
          </div>

          {/* House Size */}
          <div>
            <label className="block text-xs font-medium mb-1">House Size</label>
            <select value={houseSize} onChange={(e) => setHouseSize(e.target.value)} className="select-forms px-2 py-1 w-full">
              <option value="">All</option>
              <option value="Small">Small</option>
              <option value="Large">Large</option>
            </select>
          </div>

          {/* Tree Density */}
          <div>
            <label className="block text-xs font-medium mb-1">Trees</label>
            <select value={treeDensity} onChange={(e) => setTreeDensity(e.target.value)} className="select-forms px-2 py-1 w-full">
              <option value="">Any</option>
              <option value="None">None</option>
              <option value="Light">Light</option>
              <option value="Dense">Dense</option>
            </select>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-xs font-medium mb-1">Access</label>
            <select value={permissions} onChange={(e) => setPermissions(e.target.value)} className="select-forms px-2 py-1 w-full">
              <option value="">All</option>
              {uniquePermissions.map((permission) => (
                <option key={permission} value={permission}>
                  {permission}
                </option>
              ))}
            </select>
          </div>

          {/* Guild */}
          <div>
            <label className="block text-xs font-medium mb-1">Guild</label>
            <select value={guild} onChange={(e) => setGuild(e.target.value)} className="select-forms px-2 py-1 w-full">
              <option value="">All</option>
              {handles.map((handle) => (
                <option key={handle} value={handle}>
                  {handle}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Industry Filters - Compact version */}
        <div className="panels-filter">
          <h3 className="block text-xs font-medium mb-1">Entity Filters</h3>
          {industryFilters.map((filter, index) => (
            <div key={index} className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
              {/* Category Select */}
              <div className="sm:col-span-1 flex items-center">
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find((opt) => opt.value === filter.category)}
                  onChange={(selected) => handleCategoryChange(index, selected)}
                  placeholder="Category..."
                  className="w-full select-forms"
                  classNamePrefix="react-select"
                />
              </div>

              {/* Industry Select */}
              <div className="sm:col-span-1 flex items-center">
                <Select
                  options={filter.category ? industryOptions[filter.category] : []}
                  value={filter.category && filter.industryName ? industryOptions[filter.category]?.find((opt) => opt.value === filter.industryName) : null}
                  onChange={(selected) => {
                    const updatedFilters = [...industryFilters];
                    updatedFilters[index].industryName = selected?.value || "";
                    setIndustryFilters(updatedFilters);
                  }}
                  placeholder="Entity..."
                  className="w-full select-forms"
                  classNamePrefix="react-select"
                  isDisabled={!filter.category}
                />
              </div>

              {/* Amount Input */}
              <div className="sm:col-span-1 flex items-center">
                <input
                  type="number"
                  min="0"
                  value={filter.minAmount}
                  onChange={(e) => {
                    const updatedFilters = [...industryFilters];
                    updatedFilters[index].minAmount = parseInt(e.target.value, 10) || 0;
                    setIndustryFilters(updatedFilters);
                  }}
                  className="select-forms px-2 py-2 w-full"
                  placeholder="Min Qty"
                />
              </div>

              {/* Remove Button */}
              <div className="sm:col-span-1 flex items-center justify-end">
                <button
                  onClick={() => {
                    const updatedFilters = industryFilters.filter((_, i) => i !== index);
                    setIndustryFilters(updatedFilters.length ? updatedFilters : [{ category: "", industryName: "", minAmount: 0 }]);
                  }}
                  className="optional-button text-xs px-3 py-2"
                >
                  Remove Filter
                </button>
              </div>
            </div>
          ))}

          <div className="flex justify-between items-center mt-2">
            <button
              onClick={() => setIndustryFilters([...industryFilters, { category: "", industryName: "", minAmount: 0 }])}
              className="optional-button text-xs p-2"
            >
              + Add Filter
            </button>

            <div className="flex gap-2">
              <button onClick={() => setViewMode(viewMode === "default" ? "minimal" : "default")} className="panel-button text-xs p-2">
                {viewMode === "default" ? "Min View" : "Default View"}
              </button>
              <button onClick={clearFilters} className="panel-button text-xs p-2">
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mb-2">
        {currentLands.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {currentLands.map((land) => (
              <LandCard key={land._id} land={land} minimal={viewMode === "minimal"} />
            ))}
          </div>
        ) : (
          <div className="bg-[var(--panel-section-bg)] rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No lands found matching your filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="bg-[var(--filters-panel)] rounded-lg shadow p-2">
        <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} startPage={startPage} endPage={endPage} />
      </div>
    </div>
  );
};

export default LandLookup;
